import { useState } from "react";
import { Download, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ChatMessage as ChatMessageType } from "@/hooks/useChat";

// Detect ```document:filename\ncontent\n``` blocks
const DOC_REGEX = /```document:([^\n]+)\n([\s\S]*?)```/g;

function downloadFile(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function DocumentBlock({ filename, content }: { filename: string; content: string }) {
  const [expanded, setExpanded] = useState(false);
  const ext = filename.split(".").pop()?.toLowerCase() || "md";
  const mimeMap: Record<string, string> = {
    md: "text/markdown",
    txt: "text/plain",
    json: "application/json",
    csv: "text/csv",
    html: "text/html",
  };
  const mime = mimeMap[ext] || "text/plain";

  return (
    <div className="mt-2 rounded-lg border border-border bg-background/50 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-4 w-4 text-primary shrink-0" />
          <span className="text-xs font-medium truncate">{filename}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            onClick={() => downloadFile(filename, content, mime)}
          >
            <Download className="h-3 w-3" />
            .{ext}
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-border px-3 py-2 max-h-48 overflow-y-auto">
          <pre className="text-xs whitespace-pre-wrap font-mono text-muted-foreground">
            {content.length > 3000 ? content.slice(0, 3000) + "\n\n[...truncated]" : content}
          </pre>
        </div>
      )}
    </div>
  );
}

export function ChatMessage({ message }: { message: ChatMessageType }) {
  const isUser = message.role === "user";

  // Parse document blocks from assistant messages
  const parts: Array<{ type: "text"; text: string } | { type: "doc"; filename: string; content: string }> = [];

  if (!isUser) {
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const regex = new RegExp(DOC_REGEX.source, "g");
    while ((match = regex.exec(message.content)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: "text", text: message.content.slice(lastIndex, match.index) });
      }
      parts.push({ type: "doc", filename: match[1].trim(), content: match[2].trim() });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < message.content.length) {
      parts.push({ type: "text", text: message.content.slice(lastIndex) });
    }
  }

  // User message with attachments
  const hasAttachments = isUser && message.attachments && message.attachments.length > 0;

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground"
        }`}
      >
        {/* Show attachment thumbnails for user messages */}
        {hasAttachments && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {message.attachments!.map((att, i) =>
              att.type === "image" ? (
                <img
                  key={i}
                  src={`data:${att.media_type};base64,${att.data}`}
                  alt={att.name}
                  className="max-h-32 rounded border border-primary-foreground/20"
                />
              ) : (
                <div
                  key={i}
                  className="flex items-center gap-1 bg-primary-foreground/10 rounded px-2 py-1 text-xs"
                >
                  <FileText className="h-3 w-3" />
                  {att.name}
                </div>
              )
            )}
          </div>
        )}

        {/* Render content with document blocks */}
        {isUser || parts.length === 0 ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          parts.map((part, i) =>
            part.type === "text" ? (
              <p key={i} className="whitespace-pre-wrap">
                {part.text.trim()}
              </p>
            ) : (
              <DocumentBlock key={i} filename={part.filename} content={part.content} />
            )
          )
        )}
      </div>
    </div>
  );
}
